// ─────────────────────────────────────────────────────────────────────────────
// useCompanySettings — loads the company profile form and saves it back.
//   GET /settings/company → the form
//   PUT /settings/company → save (Admin only; adminEmail is NOT updatable)
// `save` rethrows so the caller can distinguish success from failure; the
// `error` it exposes drives the in-pane banner.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useCallback } from "react";
import { getCompanySettings, updateCompanySettings } from "../../../services/settingsService";

// API shape → form shape. Every field is a string so the inputs stay controlled.
function toForm(data) {
  return {
    workspaceName:         data.workspaceName        ?? "",
    adminEmail:            data.adminEmail           ?? "",   // read-only
    primaryContactEmail:   data.primaryContactEmail  ?? "",
    industry:              data.industry             ?? "",
    companySize:           data.companySize          ?? "",
    auditLogRetentionDays: data.auditLogRetentionDays != null
                             ? String(data.auditLogRetentionDays) : "",
  };
}

// Form shape → API payload. Empty strings become `undefined` so the backend
// treats them as "not provided" rather than "clear this field".
function toPayload(form) {
  return {
    workspaceName:         form.workspaceName       || undefined,
    primaryContactEmail:   form.primaryContactEmail || undefined,
    industry:              form.industry            || undefined,
    companySize:           form.companySize         || undefined,
    auditLogRetentionDays: form.auditLogRetentionDays
                             ? Number(form.auditLogRetentionDays) : undefined,
  };
}

export function useCompanySettings() {
  const [form,    setForm]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    getCompanySettings()
      .then(data => { if (!cancelled && data) setForm(toForm(data)); })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // setField("industry")("Finance")
  const setField = useCallback(
    (key) => (value) => setForm(prev => ({ ...prev, [key]: value })),
    [],
  );

  const save = useCallback(async () => {
    if (!form) return;
    setError(null);
    try {
      await updateCompanySettings(toPayload(form));
    } catch (err) {
      setError(err.message || "Failed to save settings.");
      throw err;
    }
  }, [form]);

  return { form, loading, error, setField, save };
}
